import cn from 'classnames'
import { ChordLib, getAllChords, GuitarLib, isChordVariant } from '@tunasong/music-lib'
import type { Chord, ChordVariant } from '@tunasong/music-lib'
import type { Scale } from '@tunasong/schemas'
import type { FC } from 'react'
import React, { useEffect, useState } from 'react'
import { GuitarChord } from '../guitar/index.js'
import {Paper, Grid, type GridProps} from '@tunasong/ui-lib'
import styles from './chord-library.module.css'

export interface ChordLibraryProps extends Omit<GridProps, 'onChange'> {
  selected?: Chord
  scale?: Scale | null
  // If specified, only show these chords. @default all chords
  chords?: ChordVariant[]

  onChange(chord: Chord): void
}

export const ChordLibrary: FC<ChordLibraryProps> = props => {
  const { selected, chords = getAllChords(), scale, onChange, ...gridProps } = props

  const handleFocus = (focusChord: ChordVariant) => () => {
    if (!ChordLib.equals(focusChord, selected)) {
      onChange(focusChord)
    }
  }

  const handleSelect = (c: Chord) => () => {
    onChange(c)
  }
  const handleKeyDown = (ch: Chord) => (ev: React.KeyboardEvent) => {
    if (ev.key === 'Enter') {
      ev.preventDefault()
      onChange(ch)
    }
  }

  /** Focus on the active chord   */
  const [activeRef, setActiveRef] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    activeRef?.focus()
  }, [activeRef])

  const showChords = chords.filter(Boolean)

  return (
    <Grid container={true} spacing={2} {...gridProps}>
      {showChords.map(ch => {
        const fingering = GuitarLib.getFingering(ch)
        const matchChord = isChordVariant(selected) ? selected : undefined

        return ch && fingering ? (
          <Grid key={ChordLib.getName(ch)}>
            <Paper
              className={cn(styles.chordPaper, ChordLib.equals(ch, matchChord) && styles.chordPaperSelected)}
              ref={ChordLib.equals(ch, matchChord) ? setActiveRef : null}
              variant="outlined"
              tabIndex={0}
              onClick={handleSelect(ch)}
              onFocus={handleFocus(ch)}
              onKeyDown={handleKeyDown(ch)}
            >
              <GuitarChord chord={ch} scale={scale} />
            </Paper>
          </Grid>
        ) : null
      })}
    </Grid>
  )
}

export default ChordLibrary
